import { useCallback, useMemo } from "react";
import { useHotelsConfig } from "@b2bportal/core-hotels";
import { ensureExhaustive } from "@b2bportal/core-utilities";
import {
  type MealPlan,
  MealPlanKind,
  type RoomProduct,
} from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";

type ExperienceDescription = {
  experienceDescription: string;
};

export type RoomPlanType =
  | ({ RoomPlanType: "ExperienceDescription" } & ExperienceDescription)
  | ({ RoomPlanType: "MealPlan" } & MealPlan);

export const useMealPlanHelpers = () => {
  const { t } = useI18nContext();

  const { enableMealPlans } = useHotelsConfig();

  const mealPlanKindNameMap: Record<MealPlanKind, string> = useMemo(
    () => ({
      [MealPlanKind.RoomOnly]: t("core-hotels.mealPlan.RoomOnly"),
      [MealPlanKind.Breakfast]: t("core-hotels.mealPlan.Breakfast"),
      [MealPlanKind.HalfBoard]: t("core-hotels.mealPlan.HalfBoard"),
      [MealPlanKind.FullBoard]: t("core-hotels.mealPlan.FullBoard"),
      [MealPlanKind.AllInclusive]: t("core-hotels.mealPlan.AllInclusive"),
      [MealPlanKind.BreakfastFor1]: t("core-hotels.mealPlan.BreakfastFor1"),
      [MealPlanKind.BreakfastFor2]: t("core-hotels.mealPlan.BreakfastFor2"),
      [MealPlanKind.Lunch]: t("core-hotels.mealPlan.Lunch"),
      [MealPlanKind.Dinner]: t("core-hotels.mealPlan.Dinner"),
    }),
    [t]
  );

  const getMealPlanNameFromRoomProduct = useCallback(
    (roomProduct: RoomProduct): string | undefined => {
      const roomPlan = getRoomPlan(roomProduct);
      if (roomPlan == null) {
        return undefined;
      }
      switch (roomPlan.RoomPlanType) {
        case "ExperienceDescription":
          return roomPlan.experienceDescription;
        case "MealPlan":
          return mealPlanKindNameMap[roomPlan.kind];
        default:
          return ensureExhaustive(roomPlan);
      }
    },
    [mealPlanKindNameMap]
  );

  const getMealPlanNameFromMealPlanKind = useCallback(
    (mealPlanKind: MealPlanKind) => {
      return mealPlanKindNameMap[mealPlanKind];
    },
    [mealPlanKindNameMap]
  );

  return useMemo(() => {
    return {
      enableMealPlans,
      handlers: {
        getMealPlanNameFromRoomProduct,
        getMealPlanNameFromMealPlanKind,
      },
    };
  }, [
    enableMealPlans,
    getMealPlanNameFromMealPlanKind,
    getMealPlanNameFromRoomProduct,
  ]);
};

export const getRoomPlan = (
  roomProduct: RoomProduct
): RoomPlanType | undefined => {
  if (roomProduct.experienceDescription != null) {
    return {
      RoomPlanType: "ExperienceDescription",
      experienceDescription: roomProduct.experienceDescription,
    };
  }

  return {
    RoomPlanType: "MealPlan",
    ...(roomProduct.mealPlan ?? { kind: MealPlanKind.RoomOnly }),
  };
};
